import React, { useState } from 'react';
import { Check, Star, Shield, Lock, CreditCard, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
type Plan = {
  id: 'basic' | 'premium';
  title: string;
  emoji: string;
  tagline: string;
  oldPrice: string;
  price: string;
  bullets: string[];
  url: string;
  featured?: boolean;
};
const PLANS: Plan[] = [{
  id: 'basic',
  title: 'SISTEMA RUNNING PRO',
  emoji: '📘',
  tagline: '6 Tomos + 6 Bonos · El sistema completo',
  oldPrice: '$180.000',
  price: '$19.990',
  bullets: ['6 Tomos + 6 Bonos', 'Protocolo de Carga Selectiva (PCS)', 'Acceso inmediato · 100% PDF'],
  url: 'https://tumanualdigital.com/cart/44204643352612:1'
}, {
  id: 'premium',
  title: 'SISTEMA RUNNING PRO + PROTOCOLO 90 DÍAS',
  emoji: '🏆',
  tagline: 'Todo el sistema + plan de ejecución día por día',
  oldPrice: '$220.000',
  price: '$27.990',
  bullets: ['Todo lo del Sistema Running Pro', 'Protocolo 90 Días con cronograma día por día', 'Tests de calibración + comparador de progreso'],
  url: 'https://tumanualdigital.com/cart/44204652527652:1',
  featured: true
}];
interface PricingSelectorProps {
  variant?: 'light' | 'dark';
}
export function PricingSelector({
  variant = 'light'
}: PricingSelectorProps) {
  const [selectedId, setSelectedId] = useState<Plan['id']>('premium');
  const selected = PLANS.find(p => p.id === selectedId)!;
  const isDark = variant === 'dark';
  return <div className="w-full max-w-2xl mx-auto" data-id="element-263">
      {/* Plan options */}
      <div role="radiogroup" aria-label="Elegí tu plan" className="space-y-3 mb-5" data-id="element-264">
        {PLANS.map(plan => {
        const isSelected = plan.id === selectedId;
        return <motion.button key={plan.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelectedId(plan.id)} whileTap={{
          scale: 0.99
        }} className={`group w-full text-left rounded-2xl p-4 md:p-5 relative transition-all border-2 ${isSelected ? 'border-[#22c55e] shadow-[0_10px_30px_rgba(34,197,94,0.25)]' : isDark ? 'border-[#1e293b] hover:border-[#22c55e]/50' : 'border-[#e2e8f0] hover:border-[#22c55e]/50'} ${isDark ? isSelected ? 'bg-gradient-to-r from-[#22c55e]/15 to-[#0f172a]' : 'bg-[#0f172a]' : isSelected ? 'bg-gradient-to-r from-[#22c55e]/8 to-white' : 'bg-white'}`} data-id="element-265">
              {plan.featured && <div className="absolute -top-2.5 right-4 bg-gradient-to-r from-[#22c55e] to-[#4ade80] text-[#020617] text-[10px] md:text-xs font-black px-2.5 py-0.5 rounded-full flex items-center gap-1 shadow-md whitespace-nowrap" data-id="element-266">
                  <Star className="w-3 h-3" fill="currentColor" data-id="element-267" /> MÁS ELEGIDO
                </div>}

              <div className="flex items-start gap-3" data-id="element-268">
                {/* Radio indicator */}
                <div className={`shrink-0 mt-0.5 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#22c55e] bg-[#22c55e]' : isDark ? 'border-[#475569] bg-transparent' : 'border-[#cbd5e1] bg-white'}`} data-id="element-269">
                  {isSelected && <Check className="text-[#020617] w-3 h-3 md:w-4 md:h-4" strokeWidth={4} data-id="element-270" />}
                </div>

                <div className="flex-grow min-w-0" data-id="element-271">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1" data-id="element-272">
                    <h3 className={`text-sm md:text-base font-black flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-[#0f172a]'}`} data-id="element-273">
                      <span data-id="element-274">{plan.emoji}</span>
                      <span className="leading-tight" data-id="element-275">{plan.title}</span>
                    </h3>
                  </div>
                  <p className={`text-xs md:text-sm font-medium mb-2 ${isDark ? 'text-[#94a3b8]' : 'text-[#475569]'}`} data-id="element-276">
                    {plan.tagline}
                  </p>

                  <div className="flex items-baseline gap-2 mb-2.5" data-id="element-277">
                    <span className="text-[#ef4444] line-through text-xs md:text-sm font-bold" data-id="element-278">
                      {plan.oldPrice}
                    </span>
                    <span className={`text-xl md:text-2xl font-black ${isSelected ? 'text-[#16a34a]' : isDark ? 'text-white' : 'text-[#0f172a]'}`} data-id="element-279">
                      {plan.price}
                    </span>
                    <span className={`text-xs font-bold ${isDark ? 'text-[#94a3b8]' : 'text-[#475569]'}`} data-id="element-280">
                      ARS
                    </span>
                  </div>

                  <ul className="space-y-1" data-id="element-281">
                    {plan.bullets.map((b, i) => <li key={i} className={`flex items-start gap-1.5 text-xs md:text-[13px] font-medium ${isDark ? 'text-[#e2e8f0]' : 'text-[#0f172a]'}`} data-id="element-282">
                        <Check className="text-[#22c55e] w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={3} data-id="element-283" />
                        <span data-id="element-284">{b}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.button>;
      })}
      </div>

      {/* Shared CTA */}
      <motion.a key={selected.id} initial={{
      scale: 0.98,
      opacity: 0.9
    }} animate={{
      scale: 1,
      opacity: 1
    }} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} href={selected.url} className="group relative w-full flex items-center justify-center text-center py-4 md:py-5 px-4 rounded-2xl font-black text-base md:text-lg bg-[#22c55e] hover:bg-[#4ade80] text-[#020617] transition-colors shadow-[0_15px_40px_rgba(34,197,94,0.4)] overflow-hidden" data-id="element-285">
        <span className="relative z-10" data-id="element-286">
          → COMPRAR AHORA · {selected.price} ARS
        </span>
        <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-287" />
      </motion.a>

      {/* Trust badges */}
      <div className={`flex flex-wrap justify-center gap-x-5 gap-y-2 mt-4 text-xs md:text-sm font-bold ${isDark ? 'text-[#e2e8f0]' : 'text-[#475569]'}`} data-id="element-288">
        <span className="flex items-center gap-1.5" data-id="element-289">
          <Lock size={14} className="text-[#22c55e]" data-id="element-290" /> Pago seguro
        </span>
        <span className="flex items-center gap-1.5" data-id="element-291">
          <Zap size={14} className="text-[#22c55e]" data-id="element-292" /> Acceso en 5 min
        </span>
        <span className="flex items-center gap-1.5" data-id="element-293">
          <Shield size={14} className="text-[#22c55e]" data-id="element-294" /> Garantía 30 días
        </span>
      </div>
      <div className={`flex justify-center mt-2 text-[11px] md:text-xs font-medium ${isDark ? 'text-[#475569]' : 'text-[#475569]'}`} data-id="element-295">
        <span className="flex items-center gap-1.5" data-id="element-296">
          <CreditCard size={12} data-id="element-297" /> Mercado Pago · Tarjeta · Transferencia
        </span>
      </div>
    </div>;
}